/**
 * Organization access grants on the user profile (users/{uid}.orgAccess).
 *
 * The server (setUserOrgAccess and the organization trigger) writes
 * orgAccess; the client only reads it:
 *   { grants: [{ orgID, level: 'view'|'member' }], grantOrgIDs,
 *     memberCompanyIDs, viewCompanyIDs, computedAt, version }
 *
 * memberCompanyIDs carry full company rights (like connectedCompanies).
 * viewCompanyIDs are read-only: the rules let them read shipments, customers,
 * addresses and invoices and refuse every write.
 */

const toIdList = (value) => {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    const out = [];
    value.forEach(raw => {
        if (raw === null || raw === undefined || typeof raw === 'object') return;
        const id = String(raw).trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        out.push(id);
    });
    return out;
};

/** The user's org-granted company ids: { member, view, all } (view excludes member ids). */
export const orgAccessCompanyIds = (userData) => {
    const access = userData && typeof userData.orgAccess === 'object' ? userData.orgAccess : null;
    if (!access) return { member: [], view: [], all: [] };
    const member = toIdList(access.memberCompanyIDs);
    const memberSet = new Set(member);
    const view = toIdList(access.viewCompanyIDs).filter(id => !memberSet.has(id));
    return { member, view, all: [...member, ...view] };
};

/** The grants on the profile, keeping only well-formed entries. */
export const orgAccessGrants = (userData) => {
    const grants = userData?.orgAccess?.grants;
    if (!Array.isArray(grants)) return [];
    return grants
        .filter(g => g && typeof g.orgID === 'string' && g.orgID.trim())
        .map(g => ({ orgID: g.orgID.trim(), level: g.level === 'member' ? 'member' : 'view' }));
};

/** True when the user holds any org grant that resolves to at least one company. */
export const hasOrgAccess = (userData) => orgAccessCompanyIds(userData).all.length > 0;

const directCompanyIds = (userData) => {
    if (!userData) return [];
    const cc = userData.connectedCompanies;
    const connected = Array.isArray(cc) ? cc : (Array.isArray(cc?.companies) ? cc.companies : []);
    return toIdList([
        ...connected,
        ...(Array.isArray(userData.companies) ? userData.companies : []),
        ...(Array.isArray(userData.hierarchyAccess?.rootCompanyIds) ? userData.hierarchyAccess.rootCompanyIds : []),
    ]);
};

/**
 * True when the user reaches this company only through a view grant: it is in
 * orgAccess.viewCompanyIDs and is neither a direct company nor a member-grant
 * company. Such a company is read-only for the user.
 */
export const isViewOnlyCompany = (userData, companyId) => {
    if (!userData || !companyId) return false;
    const id = String(companyId).trim();
    const { member, view } = orgAccessCompanyIds(userData);
    if (!view.includes(id)) return false;
    if (member.includes(id)) return false;
    return !directCompanyIds(userData).includes(id);
};
